import { Box, Button, Grid, Modal, Paper, Popover, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import MarketingEmailModel from './models/MarketingEmailModel';
import _ from 'lodash';
import { MarketingEmailSearchSelector, setMarketingEmailSearchResults, setOrderBy, setOrderDirection, setSearchResultTotalRecord, setSearchSubject, setTriggerSearch } from './reducers/MarketingEmailSearchReducer';
import { setEmailToSend, setShowEmailEditor, setTriggerEmailChange } from '../EmailEditorModal/reducers/EmailEditorReducer';
import MarketingEmailApi, { SearchMarketingEmailParam } from './api/MarketingEmailApi';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { setIsShowEmailSignatureEditor } from '../EmailSignature/reducers/EmailSignatureReducer';
import { OrderDirection } from '../Datatable/OrderDirection';
import EmailEditorModel from '../EmailEditorModal/models/EmailEditorModel';
import CompanyNamePICModel from '../CompanyNamePIC/models/CompanyNamePICModel';
import CompanyNamePICSelector from '../CompanyNamePICSelector/CompanyNamePICSelector';
import { setShowPrincipleInput } from '../SupportSheet/components/SupportSheetPrinciple/reducers/SupportSheetPrincipleReducer';
import CustomModal from '../CustomModal/CustomModal';

interface MarketingEmailTableProp {
    system: string,
}

const MarketingEmailTable: React.FunctionComponent<MarketingEmailTableProp> = (props) => {
    let dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [emailBody, setEmailBody] = useState<string>("");
    const [showErrorLogs, setShowErrorLogs] = useState<boolean>(false);
    const [errorLogs, setErrorLogs] = useState<Record<string, string>>({});

    const MarketingEmailData = useSelector(MarketingEmailSearchSelector);

    useEffect(() => {
        dispatch(showLoading(true));

        MarketingEmailApi.Search({
            system: props.system,
            subject: MarketingEmailData.searchSubject,
            body: MarketingEmailData.searchSubject,
            pageSize: 100,
            orderBy: MarketingEmailData.orderBy,
            orderDirection: MarketingEmailData.orderDirection
        } as SearchMarketingEmailParam)
            .then((response: BaseResponse<MarketingEmailModel[]>) => {
                if (response.success) {
                    dispatch(setMarketingEmailSearchResults(response.data));
                    dispatch(setSearchResultTotalRecord(response.data.length));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            })
    }, [MarketingEmailData.triggerSearch])

    const columns = [
        {
            Field: "emailTo",
            Title: "Email To",
            AllowSort: true,
            Render: (rowData: MarketingEmailModel) => (
                <>
                    {rowData.emailTo.length > 200 ? (rowData.emailTo.substring(0, 200) + "...") : rowData.emailTo}
                </>
            )
        } as DatatableColumnModel<MarketingEmailModel>,
        {
            Field: "subject",
            Title: "Subject",
            AllowSort: true,
        } as DatatableColumnModel<MarketingEmailModel>,
        {
            Field: "body",
            Title: "Email Body",
            AllowSort: true,
            Render: (rowData: MarketingEmailModel) => (
                <>
                    <Grid container>
                        <Grid item xs={6}>
                            <Button
                                onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                                    if (rowData.body && rowData.body.length > 0) {
                                        setAnchorEl(event.currentTarget);
                                        setEmailBody(rowData.body);
                                    }
                                }}
                                onMouseLeave={() => {
                                    setAnchorEl(null)
                                }}
                                disabled={!rowData.body || rowData.body.length === 0}
                            >
                                <i className="fa fa-eye" aria-hidden="true"></i>
                            </Button>
                        </Grid>
                        <Grid item xs={6}>
                            <Button
                                onClick={() => {
                                    dispatch(setEmailToSend({
                                        emailFrom: "evo@evobulk.com",
                                        emailTo: rowData.emailTo,
                                        emailSubject: rowData.subject,
                                        emailContent: rowData.body
                                    } as EmailEditorModel));

                                    dispatch(setShowEmailEditor(true));
                                }}
                            >
                                <i className="fa fa-pencil" aria-hidden="true"></i>
                            </Button>
                        </Grid>
                    </Grid>


                </>
            )
        } as DatatableColumnModel<MarketingEmailModel>,
        {
            Field: "errorLogs",
            Title: "Error Logs",
            AllowSort: true,
            Render: (rowData: MarketingEmailModel) => (
                <>
                    <Button
                        color={Object.keys(rowData.errorLogs).length > 0 ? "error" : "primary"}
                        variant="outlined"
                        onClick={() => {
                            setShowErrorLogs(true);
                            setErrorLogs(rowData.errorLogs);
                        }}>
                        {Object.keys(rowData.errorLogs).length + " error(s)"}
                    </Button>
                </>
            )
        } as DatatableColumnModel<MarketingEmailModel>,
        {
            Field: "updatedDate",
            Title: "Created Time",
            AllowSort: true,
            Render: (rowData: MarketingEmailModel) => (
                <>
                    {formatUpdatedDate(rowData.updatedDate)}
                </>
            )
        } as DatatableColumnModel<MarketingEmailModel>,
    ];

    const formatUpdatedDate = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD/MM/YYYY HH:mm");
    };

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            className="table-search-result-utility-bar"
        >
            <TextField
                label="Search"
                className='table-search-result-utility-bar-item'
                style={{ width: 250 }}
                value={MarketingEmailData.searchSubject}
                onChange={(e) => dispatch(setSearchSubject(e.target.value))}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        dispatch(setTriggerSearch((new Date()).getTime()));
                    }
                }}
            />
            <Button
                variant="contained"
                className='table-search-result-utility-bar-item'
                onClick={() => {
                    dispatch(setTriggerSearch((new Date()).getTime()));
                }}
            >Search</Button>
            <Button
                color="secondary"
                variant='outlined'
                className='table-search-result-utility-bar-item'
                onClick={() => {
                    dispatch(setEmailToSend({
                        emailFrom: "evo@evobulk.com",
                        emailTo: "",
                        emailSubject: "",
                        emailContent: ""
                    } as EmailEditorModel));

                    dispatch(setShowEmailEditor(true));
                }}
            >Compose Email</Button>
            <Button
                color="secondary"
                variant='outlined'
                className='table-search-result-utility-bar-item'
                onClick={() => {
                    dispatch(setIsShowEmailSignatureEditor(true));
                }}
            >Manage Signature</Button>
            <Grid
                className='table-search-result-utility-bar-item table-search-result-utility-bar-result-found'
            >
                {MarketingEmailData.searchResultTotalRecord + " results found"}
            </Grid>
        </Grid>
        <Datatable<MarketingEmailModel>
            useDefaultSearchBar={false}
            pagingType={PagingTypeEnum.None}
            data={MarketingEmailData.marketingEmailSearchResults}
            useItemPerPage={false}
            columns={columns}
            className='marketing-email-datatable-search-result nice-scroll'
            isDataLoaded={true}
            useHeaderSort={true}
            stickyHeader={true}
            onOrderDirectionChange={(column: DatatableColumnModel<MarketingEmailModel>, orderDirection: OrderDirection) => {
                dispatch(setOrderBy(column.Field));
                dispatch(setOrderDirection(orderDirection));
                dispatch(setTriggerSearch((new Date()).getTime()));
            }}
            orderColumn={MarketingEmailData.orderBy}
            orderDirection={MarketingEmailData.orderDirection}
        ></Datatable>
        <Popover
            id="mouse-over-popover"
            sx={{
                pointerEvents: 'none',
            }}
            open={anchorEl != null}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            disableRestoreFocus
        >
            <Typography sx={{ p: 1 }} dangerouslySetInnerHTML={{ __html: emailBody }}>
            </Typography>
        </Popover>
        <CustomModal
            open={showErrorLogs}
            onClose={() => {
                setShowErrorLogs(false);
            }}
            className='nice-scroll'
        >
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Email To</TableCell>
                            <TableCell>Error details</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(errorLogs).map(([key, value]) => (
                            <TableRow key={key}>
                                <TableCell>{key}</TableCell>
                                <TableCell>{value}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </CustomModal>
    </div>
};

export default MarketingEmailTable;